import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Zap, 
  Lock, 
  PhoneCall, 
  Cpu, 
  Sparkles, 
  DollarSign,
  Flame,
  KeyRound,
  FileCode2,
  Server
} from 'lucide-react';

export const ComparePage: React.FC = () => {
  return (
    <div className="canvas-dark" style={{ minHeight: '100vh', paddingTop: '130px', backgroundColor: '#121212' }}>
      
      {/* Hero Section */}
      <section className="container" style={{ padding: '40px 16px 60px' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
          <div className="eyebrow" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '16px' }}>
            <Zap size={14} /> Architectural benchmark &amp; comparison
          </div>
          
          <h1 className="display-large" style={{ marginBottom: '20px' }}>
            The sane alternative to <br />
            vendor lock-in and enterprise taxes
          </h1>

          <p className="body-lg" style={{ maxWidth: '760px', margin: '0 auto 32px' }}>
            Mainstream auth platforms hold password hashes hostage, charge thousands per month just to enable SAML SSO, and leave SMS endpoints vulnerable to international toll-fraud attacks.
            <br /><br />
            Draway Auth provides an open, edge-first identity platform built for high-scale SaaS engineering teams.
          </p>

          <div style={{ display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="#comparison-matrix" className="button-primary">
              View comparison matrix
            </a>
            <Link to="/docs" className="button-secondary">
              Read developer docs
            </Link>
          </div>
        </div>
      </section>

      {/* The 3 Core Industry Failures */}
      <section className="container" style={{ padding: '0 16px 70px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
          
          <div className="card-feature-dark" style={{ padding: '28px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(109, 124, 255, 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6D7CFF' }}>
                <DollarSign size={20} />
              </div>
              <h3 style={{ fontSize: '1.2rem', color: '#FFFFFF', margin: 0, fontWeight: 500 }}>The Enterprise SSO Tax</h3>
            </div>
            <p className="body-md">
              On legacy providers, the moment an enterprise customer requests Okta, Azure AD, or Google Workspace SAML, you are forced into an expensive annual enterprise contract. In reality, SAML 2.0 is standard XML signing.
            </p>
            <div style={{ marginTop: '16px', fontSize: '0.85rem', color: '#6D7CFF', fontWeight: 500 }}>
              Draway Auth includes SAML 2.0 &amp; OIDC in the Pro tier (or ₹1,200/connection) with $0 enterprise base contracts.
            </div>
          </div>

          <div className="card-feature-dark" style={{ padding: '28px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(109, 124, 255, 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6D7CFF' }}>
                <Flame size={20} />
              </div>
              <h3 style={{ fontSize: '1.2rem', color: '#FFFFFF', margin: 0, fontWeight: 500 }}>SMS Toll-Fraud Vulnerability</h3>
            </div>
            <p className="body-md">
              Automated botnets hammer standard sign-up OTP endpoints to generate kickbacks from premium carrier destinations. Traditional providers lack built-in ratio circuit breakers, leaving teams with massive telecom bills.
            </p>
            <div style={{ marginTop: '16px', fontSize: '0.85rem', color: '#6D7CFF', fontWeight: 500 }}>
              Draway Auth features an automatic verification ratio circuit breaker with WhatsApp OTP fallback.
            </div>
          </div>

          <div className="card-feature-dark" style={{ padding: '28px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(109, 124, 255, 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6D7CFF' }}>
                <Lock size={20} />
              </div>
              <h3 style={{ fontSize: '1.2rem', color: '#FFFFFF', margin: 0, fontWeight: 500 }}>Proprietary Hash Lock-In</h3>
            </div>
            <p className="body-md">
              Migrating away from legacy cloud identity services is notoriously painful because providers restrict or charge heavy export fees for raw password hashes, locking your customer base to their proprietary platform.
            </p>
            <div style={{ marginTop: '16px', fontSize: '0.85rem', color: '#6D7CFF', fontWeight: 500 }}>
              Draway Auth guarantees 1-click encrypted Argon2id export and seamless dual-write shadow migration.
            </div>
          </div>

        </div>
      </section>

      {/* Real Technical Differentiators */}
      <section className="container" style={{ padding: '0 16px 80px' }}>
        <div style={{ background: '#1A1A1A', border: '1px solid #374151', borderRadius: '8px', padding: '36px 30px' }}>
          
          <div style={{ maxWidth: '780px', marginBottom: '32px' }}>
            <div className="eyebrow" style={{ marginBottom: '8px' }}>
              Technical Architecture
            </div>
            <h2 className="heading-xl" style={{ fontSize: '1.8rem' }}>
              How Draway Auth solves real production challenges
            </h2>
            <p className="body-lg" style={{ marginTop: '8px' }}>
              Engineered from the ground up for modern edge infrastructure, resilient verification channels, and developer sovereignty.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
            
            <div style={{ padding: '24px', background: '#121212', borderRadius: '8px', border: '1px solid #374151' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <PhoneCall size={18} color="#6D7CFF" />
                <h4 style={{ color: '#FFFFFF', fontSize: '1.05rem', margin: 0, fontWeight: 500 }}>Native WhatsApp OTP Channel</h4>
              </div>
              <p className="body-md" style={{ fontSize: '0.88rem' }}>
                Carrier SMS deliverability is declining globally due to strict 10DLC filtering and telecom spam firewalls. Draway Auth integrates the official Meta Cloud API, enabling one-tap OTP delivery via WhatsApp with 98% open rates and no carrier pumping risk.
              </p>
            </div>

            <div style={{ padding: '24px', background: '#121212', borderRadius: '8px', border: '1px solid #374151' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <Cpu size={18} color="#6D7CFF" />
                <h4 style={{ color: '#FFFFFF', fontSize: '1.05rem', margin: 0, fontWeight: 500 }}>Sub-Millisecond Edge Local JWKS</h4>
              </div>
              <p className="body-md" style={{ fontSize: '0.88rem' }}>
                Don't place a 3rd-party auth service in the critical path of every API request. Draway Auth tokens are standard, stateless JWTs verified locally at the edge (Cloudflare Workers, Fastly, AWS Lambda) using cached public keys from <code>/.well-known/jwks.json</code>.
              </p>
            </div>

            <div style={{ padding: '24px', background: '#121212', borderRadius: '8px', border: '1px solid #374151' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <KeyRound size={18} color="#6D7CFF" />
                <h4 style={{ color: '#FFFFFF', fontSize: '1.05rem', margin: 0, fontWeight: 500 }}>Customer Support Impersonation</h4>
              </div>
              <p className="body-md" style={{ fontSize: '0.88rem' }}>
                Safely reproduce user issues without backdoors or token hacks. Draway generates time-bounded (15-minute) cryptographic sessions strictly restricted from modifying billing or credentials, with every request written to an immutable audit log.
              </p>
            </div>

            <div style={{ padding: '24px', background: '#121212', borderRadius: '8px', border: '1px solid #374151' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <FileCode2 size={18} color="#6D7CFF" />
                <h4 style={{ color: '#FFFFFF', fontSize: '1.05rem', margin: 0, fontWeight: 500 }}>Developer M2M API Keys Vault</h4>
              </div>
              <p className="body-md" style={{ fontSize: '0.88rem' }}>
                Issue Stripe-style API keys (<code>dw_live_...</code>) for customer developers. Features SHA-256 hashed storage at rest, fine-grained permission scopes, and per-key rate limiting out of the box.
              </p>
            </div>

            <div style={{ padding: '24px', background: '#121212', borderRadius: '8px', border: '1px solid #374151' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <Server size={18} color="#6D7CFF" />
                <h4 style={{ color: '#FFFFFF', fontSize: '1.05rem', margin: 0, fontWeight: 500 }}>Dual-Write Shadow Migration</h4>
              </div>
              <p className="body-md" style={{ fontSize: '0.88rem' }}>
                Migrate away from legacy providers without forcing your users to reset passwords. Draway's proxy validates legacy credentials on login, transparently upgrading hashes to Argon2id in your private vault with zero downtime.
              </p>
            </div>

            <div style={{ padding: '24px', background: '#121212', borderRadius: '8px', border: '1px solid #374151' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <Sparkles size={18} color="#6D7CFF" />
                <h4 style={{ color: '#FFFFFF', fontSize: '1.05rem', margin: 0, fontWeight: 500 }}>Complete Data Sovereignty</h4>
              </div>
              <p className="body-md" style={{ fontSize: '0.88rem' }}>
                Your user data belongs to you. Export your entire user catalog including encrypted Argon2id password hashes anytime with a single API call, ensuring zero platform lock-in.
              </p>
            </div>

          </div>

        </div>
      </section>

      {/* Head-to-Head Feature Comparison Matrix */}
      <section id="comparison-matrix" className="container" style={{ padding: '0 16px 90px' }}>
        <div style={{ textAlign: 'center', marginBottom: '36px' }}>
          <div className="eyebrow" style={{ marginBottom: '8px' }}>
            Specification Matrix
          </div>
          <h2 className="heading-xl">
            Feature-by-feature provider benchmark
          </h2>
          <p className="body-lg" style={{ maxWidth: '640px', margin: '8px auto 0' }}>
            Compare core protocols, verification channels, security guarantees, and migration autonomy.
          </p>
        </div>

        <div className="matrix-table-container">
          <table className="matrix-table">
            <thead>
              <tr>
                <th style={{ minWidth: '220px' }}>Capability / Architecture</th>
                <th style={{ color: '#6D7CFF', background: 'rgba(109, 124, 255, 0.08)' }}>
                  Draway Auth
                </th>
                <th>Auth0 / Okta</th>
                <th>Clerk</th>
                <th>Supabase Auth</th>
              </tr>
            </thead>
            <tbody>
              
              <tr>
                <td>
                  <strong style={{ color: '#FFFFFF' }}>SAML 2.0 / Enterprise SSO</strong>
                  <div style={{ fontSize: '0.78rem', color: '#9CA3AF', marginTop: '2px' }}>Okta, Azure AD, Google Workspace</div>
                </td>
                <td style={{ background: 'rgba(109, 124, 255, 0.04)', color: '#FFFFFF', fontWeight: 500 }}>
                  Included in Pro (₹1,200/conn on Standard)
                </td>
                <td style={{ color: '#FF5A5F' }}>Enterprise contract gate ($15k+/yr)</td>
                <td style={{ color: '#FF5A5F' }}>$100/mo add-on per B2B org</td>
                <td>$75/conn add-on</td>
              </tr>

              <tr>
                <td>
                  <strong style={{ color: '#FFFFFF' }}>SMS Toll-Fraud Shield</strong>
                  <div style={{ fontSize: '0.78rem', color: '#9CA3AF', marginTop: '2px' }}>Automated ratio circuit breaker</div>
                </td>
                <td style={{ background: 'rgba(109, 124, 255, 0.04)', color: '#FFFFFF', fontWeight: 500 }}>
                  Built-in Turnstile circuit breaker
                </td>
                <td>Basic IP rate-limiting only</td>
                <td>Basic IP rate-limiting only</td>
                <td>Manual custom implementation</td>
              </tr>

              <tr>
                <td>
                  <strong style={{ color: '#FFFFFF' }}>WhatsApp Auth &amp; OTP Channel</strong>
                  <div style={{ fontSize: '0.78rem', color: '#9CA3AF', marginTop: '2px' }}>High-deliverability Meta Cloud API</div>
                </td>
                <td style={{ background: 'rgba(109, 124, 255, 0.04)', color: '#FFFFFF', fontWeight: 500 }}>
                  Native 1st-class channel
                </td>
                <td style={{ color: '#FF5A5F' }}>Custom Twilio integration only</td>
                <td style={{ color: '#FF5A5F' }}>Not supported natively</td>
                <td>Custom Twilio hook only</td>
              </tr>

              <tr>
                <td>
                  <strong style={{ color: '#FFFFFF' }}>Password Hash Portability</strong>
                  <div style={{ fontSize: '0.78rem', color: '#9CA3AF', marginTop: '2px' }}>Zero vendor lock-in guarantee</div>
                </td>
                <td style={{ background: 'rgba(109, 124, 255, 0.04)', color: '#FFFFFF', fontWeight: 500 }}>
                  1-Click encrypted Argon2id export
                </td>
                <td style={{ color: '#FF5A5F' }}>Restricted / Enterprise gated</td>
                <td>Manual support ticket required</td>
                <td style={{ color: '#10B981' }}>Direct Postgres export</td>
              </tr>

              <tr>
                <td>
                  <strong style={{ color: '#FFFFFF' }}>Dual-Write Shadow Migration</strong>
                  <div style={{ fontSize: '0.78rem', color: '#9CA3AF', marginTop: '2px' }}>Migrate without password reset emails</div>
                </td>
                <td style={{ background: 'rgba(109, 124, 255, 0.04)', color: '#FFFFFF', fontWeight: 500 }}>
                  Automated migration proxy SDK
                </td>
                <td style={{ color: '#FF5A5F' }}>Not supported</td>
                <td style={{ color: '#FF5A5F' }}>Not supported</td>
                <td style={{ color: '#FF5A5F' }}>Custom scripts required</td>
              </tr>

              <tr>
                <td>
                  <strong style={{ color: '#FFFFFF' }}>Edge Local JWKS Verification</strong>
                  <div style={{ fontSize: '0.78rem', color: '#9CA3AF', marginTop: '2px' }}>&lt;1ms latency on Workers/Fastly/Lambda</div>
                </td>
                <td style={{ background: 'rgba(109, 124, 255, 0.04)', color: '#FFFFFF', fontWeight: 500 }}>
                  Local cached JWKS (&lt;1ms)
                </td>
                <td>Standard JWKS supported</td>
                <td style={{ color: '#FF5A5F' }}>Session round-trips to server</td>
                <td>Standard JWKS supported</td>
              </tr>

              <tr>
                <td>
                  <strong style={{ color: '#FFFFFF' }}>Support Impersonation Mode</strong>
                  <div style={{ fontSize: '0.78rem', color: '#9CA3AF', marginTop: '2px' }}>Time-bounded sessions with audit trail</div>
                </td>
                <td style={{ background: 'rgba(109, 124, 255, 0.04)', color: '#FFFFFF', fontWeight: 500 }}>
                  15m signed tokens + audit logs
                </td>
                <td style={{ color: '#FF5A5F' }}>Deprecated</td>
                <td style={{ color: '#10B981' }}>Available on Pro/B2B</td>
                <td style={{ color: '#FF5A5F' }}>Requires custom backend</td>
              </tr>

              <tr>
                <td>
                  <strong style={{ color: '#FFFFFF' }}>Developer M2M API Key Vault</strong>
                  <div style={{ fontSize: '0.78rem', color: '#9CA3AF', marginTop: '2px' }}>Stripe-style dw_live_... hashed keys</div>
                </td>
                <td style={{ background: 'rgba(109, 124, 255, 0.04)', color: '#FFFFFF', fontWeight: 500 }}>
                  Built-in scoped API key engine
                </td>
                <td style={{ color: '#FF5A5F' }}>Billed per M2M client</td>
                <td style={{ color: '#FF5A5F' }}>Not supported</td>
                <td>Requires custom table setup</td>
              </tr>

            </tbody>
          </table>
        </div>

        {/* CTA Card */}
        <div style={{ marginTop: '40px', background: '#1A1A1A', border: '1px solid #374151', borderRadius: '8px', padding: '36px', textAlign: 'center' }}>
          <h3 className="heading-xl" style={{ marginBottom: '12px' }}>
            Ready to take control of your auth stack?
          </h3>
          <p className="body-lg" style={{ maxWidth: '620px', margin: '0 auto 24px' }}>
            Join forward-thinking engineering teams building on Draway Auth. Open protocols, zero enterprise SAML markups, zero telephony fraud, zero lock-in.
          </p>
          <div style={{ display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/waitlist" className="button-primary">
              Request VIP access &rarr;
            </Link>
            <Link to="/docs" className="button-secondary">
              Read developer docs
            </Link>
          </div>
        </div>

      </section>

    </div>
  );
};

export default ComparePage;
